
// components/Reminders.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Bell, Clock, AlertTriangle, CheckCircle, 
  Calendar, User, Phone, Home, DollarSign, RefreshCw,
  X, TrendingUp, AlertCircle, Info
} from 'lucide-react';
import { useReminders } from '../hooks/useReminders';

const Reminders = () => {
  const navigate = useNavigate();
  const { reminders, loading, fetchReminders, summary, fetchSummary } = useReminders();
  const [showSummary, setShowSummary] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, []);

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getPriorityBorder = (priority) => {
    switch(priority) {
      case 'high': return 'border-red-500';
      case 'medium': return 'border-yellow-500';
      case 'low': return 'border-green-500';
      default: return 'border-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'pending': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'delivery_pending': return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'delivery_collection': return <Bell className="w-5 h-5 text-blue-500" />;
      default: return <CheckCircle className="w-5 h-5 text-green-500" />;
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'pending': return 'Payment Pending';
      case 'delivery_pending': return 'Delivery & Payment Pending';
      case 'delivery_collection': return 'Cylinder Collection Due';
      default: return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-red-100 text-red-800';
      case 'delivery_pending': return 'bg-yellow-100 text-yellow-800';
      case 'delivery_collection': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading reminders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 relative">
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-yellow-600 to-yellow-700 text-white p-4 shadow-lg sticky top-0 z-50">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')} 
              className="text-white hover:text-gray-200 transition flex items-center"
            >
              <ArrowLeft className="inline-block mr-2" size={20} />
              Back
            </button>
            <h1 className="text-2xl font-bold flex items-center">
              <Bell className="mr-2" size={24} />
              Reminders
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                fetchReminders();
                fetchSummary();
              }}
              className="bg-yellow-500 hover:bg-yellow-400 text-white px-3 py-2 rounded-md transition flex items-center"
              title="Refresh"
            >
              <RefreshCw size={18} />
            </button>
            <div className="bg-yellow-500 px-4 py-2 rounded-md">
              Total: {reminders?.length || 0} reminder{reminders?.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto p-4 md:p-6">
        {!reminders || reminders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">No Reminders! 🎉</h2>
            <p className="text-gray-600">All customers are up to date. No pending payments or overdue deliveries.</p>
            <button
              onClick={() => navigate('/customers')}
              className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-md transition"
            >
              Manage Customers
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {reminders.map((reminder) => (
              <div 
                key={reminder.customer_id} 
                className={`bg-white rounded-lg shadow-md overflow-hidden border-l-4 ${getPriorityBorder(reminder.priority)} hover:shadow-lg transition-shadow`}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-start gap-3">
                      {getStatusIcon(reminder.pending_status)}
                      <div>
                        <h3 className="text-lg font-bold text-gray-800 flex items-center">
                          <User className="w-4 h-4 mr-1" />
                          {reminder.customer_name}
                        </h3>
                        <div className="flex items-center text-gray-600 text-sm mt-1">
                          <Phone className="w-3 h-3 mr-1" />
                          {reminder.contact_number}
                        </div>
                        <div className="flex items-center text-gray-600 text-sm mt-1">
                          <Home className="w-3 h-3 mr-1" />
                          {reminder.customer_address}
                        </div>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(reminder.priority)}`}>
                      {reminder.priority.toUpperCase()}
                    </span>
                  </div>

                  {/* Status Badge */}
                  <div className="mb-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(reminder.pending_status)}`}>
                      {getStatusText(reminder.pending_status)}
                    </span>
                  </div>

                  {/* Payment Details */}
                  {reminder.total_amount > 0 && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Total Amount</p>
                          <p className="text-lg font-bold text-gray-800">₹{reminder.total_amount.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Amount Paid</p>
                          <p className="text-lg font-bold text-green-600">₹{reminder.amount_paid.toFixed(2)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Remaining</p>
                          <p className="text-lg font-bold text-red-600">₹{reminder.remaining_amount.toFixed(2)}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Date Information */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    {reminder.latest_payment_date && (
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        Last Payment: {reminder.latest_payment_date}
                      </div>
                    )}
                    {reminder.days_since_payment > 0 && (
                      <div className={`flex items-center ${
                        reminder.days_since_payment > 45 ? 'text-red-600 font-semibold' :
                        reminder.days_since_payment > 30 ? 'text-yellow-600' :
                        'text-gray-600'
                      }`}>
                        <Clock className="w-4 h-4 mr-1" />
                        {reminder.days_since_payment} days ago
                      </div>
                    )}
                    {reminder.delivery_date && (
                      <div className="flex items-center text-blue-600">
                        <Calendar className="w-4 h-4 mr-1" />
                        Delivery: {reminder.delivery_date}
                      </div>
                    )}
                    {reminder.days_since_delivery > 0 && (
                      <div className="flex items-center text-red-600 font-semibold">
                        <Clock className="w-4 h-4 mr-1" />
                        {reminder.days_since_delivery} days overdue
                      </div>
                    )}
                  </div>

                  {/* Quick Action Buttons */}
                  <div className="mt-4 flex gap-3">
                    <button
                      onClick={() => navigate('/payments')}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md transition text-sm font-semibold flex items-center justify-center"
                    >
                      <DollarSign className="w-4 h-4 mr-1" />
                      Record Payment
                    </button>
                    <button
                      onClick={() => navigate('/customers')}
                      className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md transition text-sm font-semibold flex items-center justify-center"
                    >
                      <User className="w-4 h-4 mr-1" />
                      View Customer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ SUMMARY POPUP - BOTTOM RIGHT CORNER */}
      {summary && showSummary && (
        <div className="fixed bottom-6 right-6 z-50 max-w-sm w-full">
          <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-slide-up">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 flex justify-between items-center">
              <div className="flex items-center gap-2 text-white">
                <TrendingUp size={18} />
                <h3 className="font-semibold text-sm">Reminders Summary</h3>
              </div>
              <button
                onClick={() => setShowSummary(false)}
                className="text-white/70 hover:text-white transition"
              >
                <X size={18} />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {/* Total */}
              <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                <span className="text-sm text-gray-600">Total Reminders</span>
                <span className="text-lg font-bold text-gray-800">{summary.total || 0}</span>
              </div>

              {/* Priority Stats */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-red-50 rounded-lg p-2 text-center">
                  <div className="text-xs text-red-600 font-medium">High</div>
                  <div className="text-lg font-bold text-red-700">{summary.high_priority || 0}</div>
                </div>
                <div className="bg-yellow-50 rounded-lg p-2 text-center">
                  <div className="text-xs text-yellow-600 font-medium">Medium</div>
                  <div className="text-lg font-bold text-yellow-700">{summary.medium_priority || 0}</div>
                </div>
                <div className="bg-green-50 rounded-lg p-2 text-center">
                  <div className="text-xs text-green-600 font-medium">Low</div>
                  <div className="text-lg font-bold text-green-700">{summary.low_priority || 0}</div>
                </div>
              </div>

              {/* Status Stats */}
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="bg-purple-50 rounded-lg p-2 text-center">
                  <div className="text-xs text-purple-600 font-medium">Pending Payments</div>
                  <div className="text-lg font-bold text-purple-700">{summary.pending_payments || 0}</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-2 text-center">
                  <div className="text-xs text-orange-600 font-medium">Delivery Pending</div>
                  <div className="text-lg font-bold text-orange-700">{summary.delivery_pending || 0}</div>
                </div>
                <div className="bg-teal-50 rounded-lg p-2 text-center">
                  <div className="text-xs text-teal-600 font-medium">Collection Due</div>
                  <div className="text-lg font-bold text-teal-700">{summary.delivery_collection || 0}</div>
                </div>
              </div>

              {/* Total Pending Amount */}
              <div className="bg-red-50 rounded-lg p-3 text-center border border-red-200">
                <div className="text-xs text-red-600 font-medium">Total Pending Amount</div>
                <div className="text-xl font-bold text-red-700">₹{summary.total_pending_amount?.toFixed(2) || '0.00'}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Show Summary Button (when hidden) */}
      {summary && !showSummary && (
        <button
          onClick={() => setShowSummary(true)}
          className="fixed bottom-6 right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-all hover:scale-105"
          title="Show Summary"
        >
          <Info size={24} />
        </button>
      )}

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Reminders;